import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export type StepStatus = 'pending' | 'active' | 'success' | 'error';

export interface ConnectionStep {
    label: string;
    status: StepStatus;
    /** Download progress, 0–100, while the step runs. */
    progress?: number;
    /** Short secondary text under the label, such as the amount downloaded. */
    detail?: string;
}

export interface ZitiConnectionStatusData {
    steps: ConnectionStep[];
}

export const ZITI_CONNECTION_TITLE_ID = 'ZitiConnectionTitle';

const STATE_LABEL: Record<StepStatus, string> = {
    pending: 'Waiting',
    active: 'In progress',
    success: 'Done',
    error: 'Failed',
};

@Component({
    selector: 'lib-ziti-connection-status',
    templateUrl: './ziti-connection-status.component.html',
    styleUrls: ['./ziti-connection-status.component.scss'],
    standalone: false,
})
export class ZitiConnectionStatusComponent {
    readonly titleId = ZITI_CONNECTION_TITLE_ID;
    readonly stateLabel = STATE_LABEL;
    steps: ConnectionStep[];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ZitiConnectionStatusData,
        private dialogRef: MatDialogRef<ZitiConnectionStatusComponent>
    ) {
        this.steps = data.steps;
    }

    updateStep(index: number, status: StepStatus) {
        if (this.steps[index]) {
            this.steps[index].status = status;
        }
    }

    get finished(): boolean {
        return this.steps.every((step) => step.status === 'success' || step.status === 'error');
    }

    get failed(): boolean {
        return this.steps.some((step) => step.status === 'error');
    }

    get succeeded(): boolean {
        return this.steps.every((step) => step.status === 'success');
    }

    get subtitle(): string {
        if (this.failed) {
            return 'The secure connection is unavailable, so requests use a direct connection.';
        }
        if (this.succeeded) {
            return 'Requests to the network controller now travel over the secure connection.';
        }
        return 'Requests to the network controller wait for the secure connection. Cancel to use a direct connection.';
    }

    /**
     * Close with 'cancel' so the caller stops the overlay and uses direct communication.
     */
    cancel() {
        this.dialogRef.close('cancel');
    }

    /**
     * Close with 'retry' so the caller starts a fresh connection.
     */
    retry() {
        this.dialogRef.close('retry');
    }

    close() {
        this.dialogRef.close();
    }
}
